import '@mantine/dates/styles.css';

import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Schedule } from "../../utils/schedule"
import { createToast } from "../Toast"
import { MessageKind, withLoading } from "../../utils/common"
import API from "../../utils/const"
import { authFetch } from "../../utils/session"
import { Account } from "../AccountCard"
import { Box, Button, Center, Drawer, Group, Loader, Paper, Stack, Text } from "@mantine/core"
import { IconCalendarCheck, IconUserPlus } from "@tabler/icons-react"
import { useDisclosure } from "@mantine/hooks"
import { ScheduleCard } from "./ScheduleCard"
import ScheduleForm from "./ScheduleForm"
import { handleApiError } from '../../utils/app';

export const ScheduleTab = ({
    accounts,
    accountsLoaded,
    openTab,
}: {
    accounts: Account[];
    accountsLoaded: boolean;
    openTab: (tab: string) => void;
}) => {
    const { t } = useTranslation(['app'])
    const [schedules, setSchedules] = useState<Schedule[] | null>(null)
    const [loading, setLoading] = useState(true)
    const [openedDraft, { open: openDraft, close: closeDraft }] = useDisclosure(false);
    const [selectedSchedule, setSelectedSchedule] = useState<Schedule | null>(null)

    const fetchSchedules = async () => {
        const response = await authFetch(
            `${API}/schedule`,
            {
                method: 'GET'
            }
        )
        if (!response.ok) {
            createToast(t("dashboard.error.loading-schedules"), MessageKind.Error)
            return
        }
        const schedules_ = await response.json();
        schedules_.forEach(schedule => {
            schedule.start = new Date(schedule.start)
            schedule.end = schedule.end ? new Date(schedule.end) : null
        })
        setSchedules(schedules_);
    }

    const deleteSchedule = async (schedule_uuid: string) => {
        const response = await authFetch(`${API}/schedule/${schedule_uuid}`, {
            method: 'DELETE',
        })

        if (!response.ok) {
            handleApiError(response, t, true)
            return
        }
        await fetchSchedules();
    }

    useEffect(() => {
        withLoading(setLoading)(fetchSchedules)();
    }, [])

    if (!accountsLoaded) {
        return (
            <Center my="xl">
                <Loader />
            </Center>
        );
    }

    if (!accounts || accounts.length === 0) {
        return (
            <Paper shadow="xs" p="md" radius="md" bg='gray.1' withBorder my="md">
                <Stack gap="md">
                    <Text>{t("dashboard.no-accounts-for-schedules")}</Text>
                    <Button leftSection={<IconUserPlus stroke={1} />} color='primary.4' variant="outline" onClick={() => openTab("accounts")}>
                        <Text span fw={300}>{t("dashboard.sync.connect-another-account")}</Text>
                    </Button>
                </Stack>
            </Paper>
        );
    }

    return (
        <>
            <Box my='md'>
                <Group justify='space-between'>
                    <Text className='display-5'>{t("dashboard.your-schedules")}</Text>
                    {schedules && schedules.length > 0 &&
                        <Button leftSection={<IconCalendarCheck stroke={0.7} />} color='primary.4' onClick={openDraft}><Text span fw={300}>{t("schedule.add-schedule")}</Text></Button>
                    }
                </Group>
            </Box>
            {schedules && schedules.length === 0 &&
                <Paper shadow="xs" p="md" radius="md" bg='gray.1' withBorder>
                    <Stack gap="md">
                        <Text>{t("dashboard.no-schedules")}</Text>
                        <Group>
                            <Button leftSection={<IconCalendarCheck stroke={0.7} />} color='primary.4' onClick={openDraft}><Text span fw={300}>{t("schedule.add-schedule")}</Text></Button>
                            <Button leftSection={<IconUserPlus stroke={1} />} color='primary.4' variant="outline" onClick={() => openTab("accounts")}><Text span fw={300}>{t("dashboard.sync.connect-another-account")}</Text></Button>
                        </Group>
                    </Stack>
                </Paper>
            }
            {loading && <Center my='xl'><Loader /></Center>}
            <Stack>
                {schedules && schedules.map((schedule) => {
                    return (
                        <ScheduleCard key={schedule.uuid} schedule={schedule} openEdit={() => { setSelectedSchedule(schedule); openDraft() }} onDelete={() => withLoading(setLoading)(deleteSchedule)(schedule.uuid)} />
                    )
                })}
            </Stack>
            <Drawer
                opened={openedDraft} onClose={() => { closeDraft(); setSelectedSchedule(null) }}
                withCloseButton={true}
                transitionProps={{ duration: 500 }}
                size='xl'
                title={t("schedule.add-schedule")}
            >
                <ScheduleForm close={closeDraft} schedule={selectedSchedule} accounts={accounts} successCallback={() => { withLoading(setLoading)(fetchSchedules)(); closeDraft(); }} />
            </Drawer>
        </>
    )
}